import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { taskMeta, currency, customTypeMeta, TASK_TYPES } from "@/lib/portalConstants";
import { format, parseISO, isToday, isFuture } from "date-fns";
import { Users, FileText, CalendarDays, DollarSign, CheckCircle2, Circle, ArrowRight, Loader2, MessageCircle } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, to }) => (
  <Link to={to} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-6 hover:-translate-y-0.5 transition-transform">
    <div className="flex items-center justify-between">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-sage/15 text-sage-700">
        <Icon className="h-5 w-5" />
      </span>
      <ArrowRight className="h-4 w-4 text-charcoal/30" />
    </div>
    <p className="mt-4 font-serif text-3xl text-charcoal">{value}</p>
    <p className="text-sm text-charcoal-soft">{label}</p>
  </Link>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [unreadNotes, setUnreadNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [t, c, i, tt, un] = await Promise.all([
      api.get("/portal/tasks"),
      api.get("/portal/clients"),
      api.get("/portal/invoices"),
      api.get("/portal/task-types"),
      api.get("/portal/notes/unread"),
    ]);
    setTasks(t.data);
    setClients(c.data);
    setInvoices(i.data);
    setCustomTypes(tt.data.map(customTypeMeta));
    setUnreadNotes(un.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const metaOf = (type) =>
    [...TASK_TYPES, ...customTypes].find((x) => x.value === type) || taskMeta(type);

  const toggle = async (id) => {
    await api.patch(`/portal/tasks/${id}/toggle`);
    load();
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-sage" />
      </div>
    );

  const today = tasks.filter((t) => {
    try {
      return isToday(parseISO(t.date));
    } catch {
      return false;
    }
  });
  const upcoming = tasks
    .filter((t) => {
      try {
        return isFuture(parseISO(t.date)) && !isToday(parseISO(t.date)) && !t.done;
      } catch {
        return false;
      }
    })
    .slice(0, 6);

  const outstanding = invoices
    .filter((i) => i.status !== "paid")
    .reduce((sum, i) => sum + (i.total || 0), 0);

  const clientName = (id) => clients.find((c) => c.id === id)?.name;

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <div>
        <p className="eyebrow mb-2">{format(new Date(), "EEEE, MMMM d")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-charcoal">Welcome back, {user?.name}.</h1>
      </div>

      {unreadNotes.length > 0 && (
        <div data-testid="notes-alert" className="bg-terracotta/10 border border-terracotta/25 rounded-2xl p-5 flex flex-wrap items-center gap-4">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-terracotta/15 text-terracotta shrink-0">
            <MessageCircle className="h-5 w-5" />
          </span>
          <div className="flex-1 min-w-[180px]">
            <p className="font-medium text-charcoal">New notes from family</p>
            <p className="text-sm text-charcoal-soft">Someone wrote in — tap to read and reply.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {unreadNotes.map((n) => (
              <Link key={n.client_id} to={`/portal/clients/${n.client_id}`} data-testid={`notes-alert-${n.client_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-terracotta/25 px-3.5 py-2 text-sm text-charcoal hover:border-terracotta transition-colors">
                {n.client_name}
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-terracotta text-white text-[11px] px-1.5">{n.count}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <StatCard icon={CalendarDays} label="Today's Tasks" value={today.length} to="/portal/planner" />
        <StatCard icon={Users} label="Clients" value={clients.length} to="/portal/clients" />
        <StatCard icon={FileText} label="Invoices" value={invoices.length} to="/portal/invoices" />
        <StatCard icon={DollarSign} label="Outstanding" value={currency(outstanding)} to="/portal/invoices" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today */}
        <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl text-charcoal">Today</h2>
            <Link to="/portal/planner" className="text-sm text-sage-700 hover:underline">Open planner</Link>
          </div>
          {today.length === 0 ? (
            <p className="text-sm text-charcoal-soft py-8 text-center">Nothing scheduled today. Enjoy the calm. 🌿</p>
          ) : (
            <ul className="space-y-2">
              {today.map((t) => {
                const meta = metaOf(t.type);
                return (
                  <li key={t.id} className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-sand-deep transition-colors">
                    <button onClick={() => toggle(t.id)} data-testid={`toggle-task-${t.id}`} className="text-sage-700">
                      {t.done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5 text-charcoal/30" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${t.done ? "line-through text-charcoal/40" : "text-charcoal"}`}>{t.title}</p>
                      {(t.start_time || t.client_id) && (
                        <p className="text-xs text-charcoal-soft">
                          {t.start_time} {t.client_id && `· ${clientName(t.client_id) || ""}`}
                        </p>
                      )}
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full ${meta.color}`}>{meta.label}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Upcoming */}
        <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-6">
          <h2 className="font-serif text-2xl text-charcoal mb-5">Upcoming</h2>
          {upcoming.length === 0 ? (
            <p className="text-sm text-charcoal-soft py-8 text-center">No upcoming tasks yet.</p>
          ) : (
            <ul className="space-y-2">
              {upcoming.map((t) => {
                const meta = metaOf(t.type);
                return (
                  <li key={t.id} className="flex items-center gap-3 rounded-xl px-3 py-3">
                    <span className="text-xs text-charcoal-soft w-16 shrink-0">{format(parseISO(t.date), "MMM d")}</span>
                    <p className="text-sm text-charcoal flex-1 min-w-0 truncate">{t.title}</p>
                    <span className={`text-xs px-2.5 py-1 rounded-full ${meta.color}`}>{meta.label}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
